import React, { useState, useEffect } from 'react';
import { useTable, usePagination } from 'react-table';
import { ButtonGroup, Table, Button } from 'reactstrap';
import PropTypes from 'prop-types';
import clsx from 'clsx';

const EditableCell = ({
    value: initialValue = '',
    row,
    column: { id, type },
    handleUpdate,
    editMode,
    createData
}) => {

    const [value, setValue] = useState(initialValue)

    const onChange = e => {
        setValue(e.target.value)
    }

    const onBlur = () => {
        if (row?.original?._id) handleUpdate(row.original._id, id, value, row.index)
        else createData(id, value)
    }

    useEffect(() => {
        setValue(initialValue)
    }, [initialValue])

    const showEdit = Boolean(!row || editMode === row.original._id);

    return (
        showEdit ?
            <input className="form-control" type={type === 'input' ? 'text' : type} value={value} onChange={onChange} onBlur={onBlur} /> :
            <span style={{ overflowWrap: 'anywhere' }}>{value}</span>
    )
}

const SelectableCell = ({
    value: initialValue,
    row,
    column: { id, options = [], showBlank = false },
    handleUpdate,
    editMode,
    createData
}) => {
    const [value, setValue] = useState(initialValue);

    const onChange = e => {
        const newValue = e.target.value;
        setValue(newValue);
        if (row?.original?._id) handleUpdate(row.original._id, id, newValue, row.index)
        else createData(id, newValue)
    }

    useEffect(() => {
        setValue(initialValue)
    }, [initialValue])

    const showEdit = Boolean(!row || editMode === row.original._id);

    return showEdit ?
        <select onChange={onChange} className="form-control" defaultValue={value} >
            {showBlank && <option value=''></option>}
            {options.map((p) => <option value={p.value}>{p.label}</option>)}
        </select> :
        <span style={{ overflowWrap: 'anywhere' }}>{options.find((p) => p.value === value)?.label}</span>
}

const LinkCell = ({
    row,
    column
}) => {
    return (
        <a href={column.href + row.values[column.id]} target={column.target}>{row.values[column.id]}</a>
    )
}

const defaultColumn = {
    Cell: EditableCell,
    SelectableCell,
    CheckboxCell: EditableCell,
    LinkCell,
}

function ReactTable({ columns, data, handleUpdate, handleDelete, setnewData, handleCreate, showCreate, setshowCreate, newData, readOnly, className, onPageChange, onRowClick }) {

    const [editMode, seteditMode] = useState(false);
    const handleEdit = (typeId) => seteditMode(typeId !== editMode ? typeId : '');
    const createData = (id, value) => setnewData({ ...newData, [id]: value });

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            createData,
            columns,
            data,
            defaultColumn,
            handleUpdate,
            handleDelete,
            editMode
        },
        usePagination
    )

        useEffect(() => {
            onPageChange?.(page)
        }, [page, onPageChange])

    return (
        <Table {...getTableProps()} responsive hover className={className}>
            <thead>
                {headerGroups.map(headerGroup => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map(column => (
                            <th {...column.getHeaderProps()} >{column.render('Header')}</th>
                        ))}
                        <th className={clsx('text-center', readOnly && 'd-none')}></th>
                    </tr>
                ))}
            </thead>
            <tbody {...getTableBodyProps()}>
                {<>
                    {showCreate && <tr>
                        {headerGroups[1].headers.map(column => (
                            {
                                'checkbox': <td {...column.getHeaderProps()}>{column.render('CheckboxCell', { value: newData[column.id] })}</td>,
                                'select': <td {...column.getHeaderProps()}>{column.render('SelectableCell', { value: newData[column.id] })}</td>,
                                'input': <td {...column.getHeaderProps()}>{column.render('Cell', { value: newData[column.id] })}</td>,
                            }[column.type]
                        ))}
                        <td className={clsx('text-center', readOnly && 'd-none')} style={{ maxWidth: '40px' }}>
                            <ButtonGroup>
                                <Button
                                    // color="success"
                                    outline
                                    onClick={handleCreate}
                                ><i className="fas fa-check"></i>
                                </Button>
                                <Button
                                    // color="danger"
                                    outline
                                    onClick={() => setshowCreate(false)}
                                ><i className="fas fa-times"></i>
                                </Button>
                            </ButtonGroup>
                        </td>
                    </tr>
                    }
                    {page.map((row, i) => {
                        prepareRow(row);
                        const handleRowClick = () => {
                            onRowClick?.(row.original)
                        }
                        return (
                            <tr {...row.getRowProps()} onClick={handleRowClick}>
                                {row.cells.map(cell => (
                                    {
                                        'checkbox': <td {...cell.getCellProps()}>{cell.render('CheckboxCell')}</td>,
                                        'select': <td {...cell.getCellProps()}>{cell.render('SelectableCell')}</td>,
                                        'input': <td {...cell.getCellProps()}>{cell.render('Cell')}</td>,
                                        'link': <td {...cell.getCellProps()}>{cell.render('LinkCell')}</td>,
                                    }[cell.column.type]
                                ))}
                                <td className={clsx('text-center', readOnly && 'd-none')} style={{ maxWidth: '40px' }}>
                                    <ButtonGroup>
                                        <Button
                                            // color="success"
                                            outline
                                            onClick={() => handleEdit(row.original._id)}><i className="fas fa-edit"></i>
                                        </Button>
                                        <Button
                                            // color="danger"
                                            outline
                                            onClick={() => handleDelete(row.original._id)}><i className="fas fa-times"></i>
                                        </Button>
                                    </ButtonGroup>
                                </td>
                            </tr>
                        )
                    })}
                </>}
            </tbody>
            <tfoot style={{ borderTop: '2px solid #ddd' }}>
                <tr >
                    <td colSpan={columns?.[0]?.columns?.length + 1 || columns.length} className="text-center">
                        <button className="btn btn-sm mx-2" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                            <i className="fas fa-chevron-left"></i><i className="fas fa-chevron-left"></i>
                        </button>
                        <button className="btn btn-sm mx-2" onClick={() => previousPage()} disabled={!canPreviousPage}>
                            <i className="fas fa-chevron-left"></i>
                        </button>
                        <span className="mx-2">
                            <strong>
                                {pageIndex + 1} / {pageOptions.length}
                            </strong>
                        </span>
                        <button className="btn btn-sm mx-2" onClick={() => nextPage()} disabled={!canNextPage}>
                            <i className="fas fa-chevron-right"></i>
                        </button>
                        <button className="btn btn-sm mx-2" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
                            <i className="fas fa-chevron-right"></i><i className="fas fa-chevron-right"></i>
                        </button>
                        {/* ({data.length}) */}
                        <select
                            value={pageSize}
                            onChange={e => {
                                setPageSize(Number(e.target.value))
                            }}
                        >
                            {[10, 20, 30, 40, 50].map(pageSize => (
                                <option key={pageSize} value={pageSize}>
                                    {pageSize}
                                </option>
                            ))}
                        </select>
                    </td>
                </tr>
            </tfoot>
        </Table>
    )
}

ReactTable.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.any),
    data: PropTypes.arrayOf(PropTypes.any),
    handleUpdate: PropTypes.func,
    handleDelete: PropTypes.func,
    setnewData: PropTypes.func,
    handleCreate: PropTypes.func,
    showCreate: PropTypes.bool,
    setshowCreate: PropTypes.func,
    readOnly: PropTypes.bool,
    className: PropTypes.string,
    newData: PropTypes.objectOf(PropTypes.any)
};

export default ReactTable;